import { usePathname, useParams, useSearchParams } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../stores/useAppStore';
import { projectQueryKeys } from './queries/useProjects';
import { customerQueryKeys } from './queries/useCustomers';
import { orderQueryKeys } from './queries/useOrders';
import { batchQueryKeys } from './queries/useBatches';
import { taskQueryKeys } from './queries/useTasks';
import { productionStageQueryKeys } from './queries/useProductionStages';
import type { Project, Customer, Order, Batch, Task, ProductionStage } from '../supabase/types';

export interface PageContext {
  // Navigation
  currentPath: string;
  pageType: string;

  // Entities from URL/route
  entities: {
    projectId?: string;
    projectName?: string;
    orderId?: string;
    orderName?: string;
    batchId?: string;
    batchName?: string;
    taskId?: string;
    taskName?: string;
    productionStageId?: string;
    productionStageName?: string;
    customerId?: string;
    customerName?: string;
    supplierId?: string;
    supplierName?: string;
    qualityCheckId?: string;
    qualityCheckSerialNumber?: string;
  };

  // User context
  user: {
    role?: string;
    currentCompanyId?: string;
  };
}

function getPageType(pathname: string, searchParams: URLSearchParams | null): string {
  // Dashboard pages
  if (pathname === '/dashboard') return 'dashboard_home';
  if (pathname.includes('/dashboard/projects')) {
    if (pathname.includes('/new')) return 'project_create';
    if (pathname.includes('/reports')) {
      // Check if viewing a specific report
      if (searchParams?.get('qualityCheck')) return 'quality_check_report_detail';
      return 'projects_quality_check_reports';
    }
    if (pathname.includes('/orders')) {
      // Check if viewing/editing a specific order
      const orderId = searchParams?.get('order');
      if (orderId === 'new') return 'order_create';
      if (orderId) return 'order_detail';
      return 'projects_orders';
    }
    if (pathname.includes('/batches')) {
      // Check if viewing/editing a specific batch
      const batchId = searchParams?.get('batch');
      if (batchId === 'new') return 'batch_create';
      if (batchId) return 'batch_detail';
      return 'projects_batches';
    }
    if (pathname.includes('/production-stages')) {
      // Check for checklist view
      if (pathname.includes('/checklist')) return 'production_stage_checklist';
      // Check if viewing/editing a specific stage
      const stageId = searchParams?.get('stage');
      if (stageId === 'new') return 'production_stage_create';
      if (stageId) return 'production_stage_detail';
      return 'projects_production_stages';
    }
    if (pathname.includes('/documents')) return 'projects_documents';
    // Check if we're on a project detail page (has ID but no sub-route)
    if (pathname.match(/\/dashboard\/projects\/[^\/]+\/?$/)) return 'project_page';
    return 'projects_list';
  }

  // Tasks
  if (pathname.includes('/dashboard/tasks')) {
    const taskId = searchParams?.get('task');
    if (taskId === 'new') return 'task_create';
    if (taskId) return 'task_detail';
    return 'tasks_list';
  }

  // Suppliers
  if (pathname.includes('/dashboard/suppliers-database')) {
    const supplierId = searchParams?.get('supplier');
    if (supplierId === 'new') return 'supplier_create';
    if (supplierId) return 'supplier_detail';
    return 'suppliers_list';
  }

  // Settings
  if (pathname.includes('/dashboard/settings')) {
    if (pathname.includes('/company-management')) return 'settings_company';
    if (pathname.includes('/users-control')) return 'settings_users';
    if (pathname.includes('/customers')) {
      const customerId = searchParams?.get('customer');
      if (customerId === 'new') return 'customer_create';
      if (customerId) return 'customer_detail';
      return 'settings_customers';
    }
    if (pathname.includes('/manufacturing-facilities')) {
      const facilityId = searchParams?.get('facility');
      if (facilityId === 'new') return 'facility_create';
      if (facilityId) return 'facility_detail';
      return 'settings_facilities';
    }
    return 'settings_general';
  };

  // Time management
  if (pathname.includes('/dashboard/time-management')) return 'time_management';

  return 'unknown';
}

export function usePageContext(): PageContext {
  const pathname = usePathname();
  const params = useParams();
  
  // Handle useSearchParams safely for SSR/static generation
  let searchParams: ReturnType<typeof useSearchParams> | null = null;
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    searchParams = useSearchParams();
  }
  
  const { selectedCompanyId, selectedCompanyUser } = useAppStore();
  const queryClient = useQueryClient();

  // Build entities object with IDs and names
  const entities: PageContext['entities'] = {};

  // Helper function to safely get cached data without triggering fetches
  const getCachedData = <T,>(queryKey: readonly unknown[]): T | undefined => {
    return queryClient.getQueryData<T>(queryKey);
  };

  // Get cached data (won't trigger any fetches)
  const projects = getCachedData<Project[]>(projectQueryKeys.byCompany(selectedCompanyId || ''));
  const customers = getCachedData<Customer[]>(customerQueryKeys.byCompany(selectedCompanyId || ''));

  // Project context
  if (params?.id && typeof params.id === 'string') {
    entities.projectId = params.id;
    
    // Try to get from cache - check multiple possible cache keys
    const project = projects?.find(p => p.id === params.id) ||
                   getCachedData<Project>(projectQueryKeys.detail(params.id));
    
    if (project) {
      entities.projectName = project.name;

      // Also get customer and manufacturer names for the project
      if (project.customer_id) {
        entities.customerId = project.customer_id;
        const customer = customers?.find(c => c.id === project.customer_id) ||
                        getCachedData<Customer>(customerQueryKeys.detail(project.customer_id));
        if (customer) {
          entities.customerName = customer.name;
        }
      }
    }
  }

  // Order context - check both URL params and search params
  const orderIdFromParams = params?.orderId as string | undefined;
  const orderIdFromSearch = searchParams?.get('order');
  const orderId = orderIdFromParams || (orderIdFromSearch && orderIdFromSearch !== 'new' ? orderIdFromSearch : null);
  
  if (orderId) {
    entities.orderId = orderId;
    
    // Check cache for orders - try project-specific or detail query
    const orders = params.id ? 
      getCachedData<Order[]>(orderQueryKeys.byProject(params.id as string)) :
      null;
    
    const order = orders?.find(o => o.id === orderId) ||
                 getCachedData<Order>(orderQueryKeys.detail(orderId));
    
    if (order) {
      entities.orderName = order.name;
    }
  }

  // Batch context - check both URL params and search params
  const batchIdFromParams = params?.batchId as string | undefined;
  const batchIdFromSearch = searchParams?.get('batch');
  const batchId = batchIdFromParams || (batchIdFromSearch && batchIdFromSearch !== 'new' ? batchIdFromSearch : null);
  
  if (batchId) {
    entities.batchId = batchId;
    
    // Check cache for batches - try project-specific, order-specific or detail query
    let batches: Batch[] | undefined;
    if (params.id) {
      batches = getCachedData<Batch[]>(batchQueryKeys.byProject(params.id as string));
    }
    if (!batches && orderId) {
      batches = getCachedData<Batch[]>(batchQueryKeys.byOrder(orderId));
    }
    
    const batch = batches?.find(b => b.id === batchId) ||
                 getCachedData<Batch>(batchQueryKeys.detail(batchId));
    
    if (batch) {
      entities.batchName = batch.name;
    }
  }

  // Task context - check both URL params and search params
  const taskIdFromParams = params?.taskId as string | undefined;
  const taskIdFromSearch = searchParams?.get('task');
  const taskId = taskIdFromParams || (taskIdFromSearch && taskIdFromSearch !== 'new' ? taskIdFromSearch : null);
  
  if (taskId) {
    entities.taskId = taskId;
    
    const tasks = getCachedData<Task[]>(taskQueryKeys.byCompany(selectedCompanyId || ''));
    const task = tasks?.find(t => t.id === taskId) ||
                getCachedData<Task>(taskQueryKeys.detail(taskId));
    
    if (task) {
      entities.taskName = task.title;
    }
  }

  // Production stage context - check both URL params and search params
  const stageIdFromParams = (params?.stageId || params?.productionStageId) as string | undefined;
  const stageIdFromSearch = searchParams?.get('stage');
  const productionStageId = stageIdFromParams || (stageIdFromSearch && stageIdFromSearch !== 'new' ? stageIdFromSearch : null);
  
  if (productionStageId) {
    entities.productionStageId = productionStageId;
    
    if (params.id) {
      const productionStages = getCachedData<ProductionStage[]>(
        productionStageQueryKeys.byProject(params.id as string)
      );
      const productionStage = productionStages?.find(ps => ps.id === productionStageId) ||
                             getCachedData<ProductionStage>(productionStageQueryKeys.detail(productionStageId));
      if (productionStage) {
        entities.productionStageName = productionStage.name;
      }
    }
  }

  // Customer context - from search params (used in settings)
  const customerIdFromSearch = searchParams?.get('customer');
  if (customerIdFromSearch && customerIdFromSearch !== 'new') {
    entities.customerId = customerIdFromSearch;
    const customer = customers?.find(c => c.id === customerIdFromSearch) ||
                    getCachedData<Customer>(customerQueryKeys.detail(customerIdFromSearch));
    if (customer) {
      entities.customerName = customer.name;
    }
  }

  // Supplier context - from search params
  const supplierIdFromSearch = searchParams?.get('supplier');
  if (supplierIdFromSearch && supplierIdFromSearch !== 'new') {
    entities.supplierId = supplierIdFromSearch;
    // Note: Add supplier name resolution if supplier queries are available
  }

  // Quality check context - from search params
  const qualityCheckIdFromSearch = searchParams?.get('qualityCheck');
  if (qualityCheckIdFromSearch && qualityCheckIdFromSearch !== 'new') {
    entities.qualityCheckId = qualityCheckIdFromSearch;
    // Note: Add quality check details resolution if quality check queries are available
    // For now, we can try to get the serial number from the quality check
    // This would require importing qualityCheckQueryKeys and the QualityCheck type
  }

  return {
    currentPath: pathname || '/',
    pageType: getPageType(pathname || '/', searchParams),
    entities,
    user: {
      role: selectedCompanyUser?.role,
      currentCompanyId: selectedCompanyId || undefined,
    },
  };
}